


/**
 * @typedef {object} AccountsController.$Scope
 * @property {boolean} showArchivedAccountsList
 * @property {boolean} isLinkedAccountListDisabled
 * @property {unknown[]} providerAccounts
 * @property {unknown[]} providerAccountsWithArchives
 * @property {import("javascripts/features").FeatureFlags} FEATURES
 *
 * @property {() => ng.IPromise<unknown>} doAddAccounts
 * @property {(providerAccount: import('types/api').ProviderAccountFromAPI) => ng.IPromise<unknown>} doRefreshProviderAccount
 * @property {(providerAccount: import('types/api').ProviderAccountFromAPI) => ng.IPromise<unknown>} doEditProviderAccount
 * @property {(providerAccount: import('types/api').ProviderAccountFromAPI) => ng.IPromise<unknown>} doReconnectProviderAccount
 * @property {(account: import('types/api').AccountFromAPI, name: string) => ng.IPromise<unknown>} doRenameAccount
 * @property {(account: import('types/api').AccountFromAPI) => ng.IPromise<unknown>} doRemoveAccount
 */

import { combineLatest } from "rxjs";


/**
 * @typedef {ng.IScope
 *   & import('javascripts/collapsible.controller').IScope
 *   & AccountsController.$Scope
 * } AccountsController.ScopeArg
 */

/**
 * @name AccountsController
 * @constructor
 * @param {AccountsController.ScopeArg} $scope
 * @param {import('core-services/provider-accounts.service').ProviderAccountsService} ProviderAccountsService
 * @param {import('./view-services/provider-account-view-model.service').ProviderAccountViewModelService} ProviderAccountViewModelService
 * @param {import('core-services/user.service').UserService} UserService
 * @param {import('../fastlink/fastlink.service').FastlinkService} FastlinkService
 * @param {import('../features').FeatureFlags} FEATURES
 */
export function AccountsController(
  $scope,
  removeAccountService,
  ProviderAccountsService,
  ProviderAccountViewModelService,
  UserService,
  FastlinkService,
  FEATURES,
) {
  if ($scope.setControllerName)
    $scope.setControllerName('AccountsCtrl');

  $scope.showArchivedAccountsList = false;
  $scope.isLinkedAccountListDisabled = false;
  $scope.providerAccounts = [];
  $scope.providerAccountsWithArchives = [];


  /**
   * When `providerAccounts` on parent scope changes, we need to build
   * ViewModels for this controller.
   */
  combineLatest(
    ProviderAccountsService.providerAccounts$,
    ProviderAccountsService.providerAccountsWithArchives$,
  ).subscribe(([providerAccounts, providerAccountsWithArchives]) => {
    $scope.$applyAsync((scope) => {
      $scope.providerAccounts             = angular.copy(providerAccounts);
      $scope.providerAccountsWithArchives = angular.copy(providerAccountsWithArchives);
      $scope.showArchivedAccountsList     = providerAccountsWithArchives.length > 0;
    });
  });

  $scope.FEATURES = FEATURES;

  /**
   * Triggers an add flow
   */
  $scope.doAddAccounts = function() {
    return triggerFastlink({ action: 'add' });
  };

  /**
   * Triggers a refresh flow
   *
   * @param {import('../../types/api').ProviderAccountFromAPI} providerAccount
   */
  $scope.doRefreshProviderAccount = function(providerAccount) {
    return triggerFastlink({ providerAccountId: providerAccount.id, action: 'refresh' });
  }

  /**
   * Triggers an edit-credentials flow
   *
   * @param {import('../../types/api').ProviderAccountFromAPI} providerAccount
   */
  $scope.doEditProviderAccount = function(providerAccount) {
    return triggerFastlink({ providerAccountId: providerAccount.id, action: 'edit' });
  }

  /**
   * Triggers a reconnect flow
   *
   * @param {import('../../types/api').ProviderAccountFromAPI} providerAccount
   */
  $scope.doReconnectProviderAccount = function(providerAccount) {
    return triggerFastlink({ providerAccountId: providerAccount.id, action: 'reconnect' });
  };


  /**
   * Handles a UX event indicating that the account should be
   * renamed.
   */
  $scope.doRenameAccount = function(account, name) {
    return ProviderAccountsService.accountWasRenamed(account, name);
  };

  /**
   * Triggers an Account Removal prompt (dialog) via the
   * `removeAccountService` dependency.
   */
  $scope.doRemoveAccount = function(account) {
    var accountVM = ProviderAccountViewModelService.buildAccountModel(account);

    return removeAccountService
      .removeAccountWithPrompt(accountVM)
      .then(function(res) {
        $scope.$applyAsync();
        return res;
      });
  };

  /**
   * @param {import("../../types/api").V2.FetchFastlinkStrategy.Request} fastlinkInfoRequest
   */
  async function triggerFastlink(fastlinkInfoRequest) {
    UserService.fetchFastlinkInfo(fastlinkInfoRequest)
      .then(fastlinkStrategy => FastlinkService.openFastlink(fastlinkStrategy))
      .then(data => UserService.postFastlinkV3Success(data));
  }

  UserService.user$.subscribe(_ => {
    $scope.isLinkedAccountListDisabled = _?.state == 'setup';
  })
};

export default [
  '$scope',
  'removeAccountService',
  'ProviderAccountsService',
  'ProviderAccountViewModelService',
  'UserService',
  'FastlinkService',
  'FEATURES',
  AccountsController
];